import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, pingEndpoint, logout } from '../../admin/api';
import BookingsPanel from '../../admin/BookingsPanel';
import TeamPanel from '../../admin/TeamPanel';
import DeployPanel from '../../admin/DeployPanel';
import MarketingPanel from '../../admin/MarketingPanel';
import RewardsPanel from '../../admin/RewardsPanel';
import Icon from '../../admin/Icon';
import ThemeToggle from '../../admin/ThemeToggle';
import '../../admin/admin.css';

const PUBLIC_ENDPOINTS = ['/api/test', '/api/verify', '/api/sms-reply'];
const REFRESH_MS = 15000;

const NAV = [
  { key: 'overview', label: 'Overview', icon: 'chart', sub: 'System health & activity' },
  { key: 'bookings', label: 'Bookings', icon: 'calendar', sub: 'Schedule & drag-to-reschedule' },
  { key: 'team', label: 'Team', icon: 'users', sub: 'Invite & manage shop managers' },
  { key: 'marketing', label: 'Marketing', icon: 'message', sub: 'Send SMS promos to customers' },
  { key: 'rewards', label: 'Rewards', icon: 'gift', sub: 'Points & loyalty program' },
  { key: 'deploy', label: 'Deploy', icon: 'rocket', sub: 'Push website & server updates' },
];

const fmtUptime = (s) => {
  if (s == null) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return [d && `${d}d`, h && `${h}h`, `${m}m`].filter(Boolean).join(' ');
};
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : 'never');
const timeAgo = (iso) => {
  if (!iso) return null;
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return `${d}d ago`;
};
const Light = ({ ok }) => <span className={`light ${ok ? 'ok' : 'bad'}`} />;

const Overview = ({ navigate }) => {
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState({ events: [], errors: [], sms: [] });
  const [pings, setPings] = useState([]);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [diag, setDiag] = useState({});
  const [bookingTest, setBookingTest] = useState(null);

  const runBookingTest = async () => {
    setBookingTest({ state: 'busy' });
    try {
      const r = await api.testBooking();
      setBookingTest({ state: r.smsSent ? 'ok' : 'err', msg: r.message });
    } catch (e) {
      setBookingTest({ state: 'err', msg: e.message });
    }
  };

  const runTest = async (kind) => {
    setDiag((d) => ({ ...d, [kind]: { state: 'busy' } }));
    try {
      const r = kind === 'sms' ? await api.testSms() : await api.testEmail();
      setDiag((d) => ({ ...d, [kind]: { state: 'ok', msg: r.message || 'Sent.' } }));
    } catch (e) {
      setDiag((d) => ({ ...d, [kind]: { state: 'err', msg: e.message } }));
    }
  };

  const renderTest = (kind, label) => {
    const st = diag[kind];
    return (
      <div className="hc-test">
        <button className="admin-ghost-btn hc-test-btn" disabled={st?.state === 'busy'} onClick={() => runTest(kind)}>
          {st?.state === 'busy' ? <span className="spinner sm" /> : <Icon name="zap" size={14} />}
          {st?.state === 'busy' ? 'Sending…' : label}
        </button>
        {st && st.state !== 'busy' && (
          <p className={st.state === 'ok' ? 'hc-test-ok' : 'hc-test-err'}>
            <Icon name={st.state === 'ok' ? 'check-circle' : 'alert'} size={13} /> {st.msg}
          </p>
        )}
      </div>
    );
  };

  const refresh = useCallback(async () => {
    try {
      const [h, l, p] = await Promise.all([
        api.health(), api.logs(), Promise.all(PUBLIC_ENDPOINTS.map(pingEndpoint)),
      ]);
      setHealth(h); setLogs(l); setPings(p); setUpdatedAt(new Date()); setError('');
    } catch (err) {
      setError(err.message);
      if (/log in/i.test(err.message)) navigate('/portal/login', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const db = health?.database;
  const tw = health?.integrations?.twilio;
  const em = health?.integrations?.email;

  return (
    <div className="overview">
      <div className="overview-head">
        <p className="admin-subtle">{updatedAt ? `Updated ${updatedAt.toLocaleTimeString()}` : 'Loading…'} · auto-refresh 15s</p>
        <button className="admin-ghost-btn" onClick={refresh}><Icon name="refresh" size={15} /> Refresh now</button>
      </div>

      {error && <p className="admin-error"><Icon name="alert" size={15} /> {error}</p>}

      <div className="health-grid">
        <div className="health-card">
          <div className="hc-head"><span className="hc-icon"><Icon name="server" size={18} /></span><h3>Server</h3><Light ok={health?.server?.ok} /></div>
          <div className="hc-big">{health?.server?.ok ? 'Online' : 'Unknown'}</div>
          <p>Uptime · {fmtUptime(health?.server?.uptimeSeconds)}</p>
          <p>Errors logged · {health?.errorCount ?? '—'}</p>
        </div>
        <div className="health-card">
          <div className="hc-head"><span className="hc-icon"><Icon name="database" size={18} /></span><h3>Database</h3><Light ok={db?.ok} /></div>
          <div className="hc-big">{db?.ok ? `${db.latencyMs} ms` : 'Down'}</div>
          <p>{db?.ok ? 'Connected' : (db?.error || 'No connection')}</p>
          <p>Total · {db?.totalBookings ?? '—'} · Today · {db?.todayBookings ?? '—'}</p>
        </div>
        <div className="health-card">
          <div className="hc-head"><span className="hc-icon"><Icon name="message" size={18} /></span><h3>Twilio SMS</h3><Light ok={tw?.configured && !tw?.lastError} /></div>
          <div className="hc-big">{tw?.configured ? 'Active' : 'Off'}</div>
          <p>Last OK · {fmtTime(tw?.lastOk?.at)}</p>
          {tw?.lastReplyAt
            ? <p>Last reply · {timeAgo(tw.lastReplyAt)}</p>
            : <p className="muted-err"><Icon name="alert-triangle" size={12} /> No replies received — check the Twilio webhook URL</p>}
          {tw?.lastError && <p className="muted-err">Error · {tw.lastError.message}</p>}
          {renderTest('sms', 'Send test SMS')}
        </div>
        <div className="health-card">
          <div className="hc-head"><span className="hc-icon"><Icon name="mail" size={18} /></span><h3>Email</h3><Light ok={em?.configured && !em?.lastError} /></div>
          <div className="hc-big">{em?.configured ? 'Active' : 'Off'}</div>
          <p>Last OK · {fmtTime(em?.lastOk?.at)}</p>
          {em?.lastError && <p className="muted-err">Error · {em.lastError.message}</p>}
          {renderTest('email', 'Send test email')}
        </div>
      </div>

      <div className="diag-card">
        <div className="diag-card-info">
          <h3><Icon name="check-circle" size={16} /> Booking system test</h3>
          <p className="admin-subtle">Creates a real test booking to <strong>(612) 987-3615</strong> and texts the confirm prompt to it. Reply <code>Y&lt;id&gt;</code> from that phone to verify the full booking → confirm → customer-text flow, then cancel the test booking in Bookings.</p>
        </div>
        <div className="diag-card-action">
          <button className="admin-primary-btn" onClick={runBookingTest} disabled={bookingTest?.state === 'busy'}>
            {bookingTest?.state === 'busy' ? <span className="spinner sm" /> : <Icon name="zap" size={15} />}
            {bookingTest?.state === 'busy' ? 'Creating…' : 'Send test booking'}
          </button>
          {bookingTest && bookingTest.state !== 'busy' && (
            <p className={bookingTest.state === 'ok' ? 'hc-test-ok' : 'hc-test-err'}>
              <Icon name={bookingTest.state === 'ok' ? 'check-circle' : 'alert'} size={13} /> {bookingTest.msg}
            </p>
          )}
        </div>
      </div>

      <h2 className="section-title">API endpoints</h2>
      <div className="table-wrap">
        <table className="bookings-table">
          <thead><tr><th>Endpoint</th><th>Status</th><th>HTTP</th><th>Response</th></tr></thead>
          <tbody>
            {pings.map((p) => (
              <tr key={p.path}>
                <td className="cell-strong">{p.path}</td>
                <td><Light ok={p.ok} /> {p.ok ? 'OK' : 'Fail'}</td>
                <td>{p.status || '—'}</td>
                <td>{p.ms} ms</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="logs-grid">
        <div>
          <h2 className="section-title">Recent activity</h2>
          <div className="log-feed">
            {logs.events.length ? logs.events.map((e, i) => (
              <div key={i} className="log-line">
                <span className="log-time">{new Date(e.at).toLocaleTimeString()}</span>
                <span className={`log-tag tag-${e.type}`}>{e.type}</span>
                <span>{e.message}</span>
              </div>
            )) : <p className="empty-row">No activity yet.</p>}
          </div>
        </div>
        <div>
          <h2 className="section-title">Recent errors</h2>
          <div className="log-feed">
            {logs.errors.length ? logs.errors.map((e, i) => (
              <div key={i} className="log-line error">
                <span className="log-time">{new Date(e.at).toLocaleTimeString()}</span>
                <span className="log-tag tag-error">{e.source}</span>
                <span>{e.message}</span>
              </div>
            )) : <p className="empty-row"><Icon name="check-circle" size={15} /> No errors logged.</p>}
          </div>
        </div>
      </div>

      <div className="overview-head" style={{ marginTop: 26 }}>
        <h2 className="section-title" style={{ margin: 0 }}><Icon name="message" size={16} /> SMS activity</h2>
        <span className="admin-subtle">Confirm/cancel replies & alert sends · newest first</span>
      </div>
      <div className="log-feed sms-feed">
        {logs.sms && logs.sms.length ? logs.sms.map((m, i) => (
          <div key={i} className={`log-line ${m.ok === false ? 'error' : ''}`}>
            <span className="log-time">{new Date(m.at).toLocaleTimeString()}</span>
            <span className={`log-tag ${m.direction === 'out' ? 'tag-sms-out' : 'tag-sms-in'}`}>{m.direction === 'out' ? 'SENT' : 'REPLY'}</span>
            <span className="sms-num">{m.from || m.to || '—'}</span>
            <span className="sms-result">
              {m.ok === false && <Icon name="alert" size={13} />} {m.result || m.body}
              {m.direction === 'in' && m.body ? <em className="sms-body"> · “{m.body}”</em> : null}
            </span>
          </div>
        )) : <p className="empty-row"><Icon name="check-circle" size={15} /> No SMS activity recorded yet.</p>}
      </div>
    </div>
  );
};

const WebmasterDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [mobileNav, setMobileNav] = useState(false);
  const handleLogout = () => { logout(); navigate('/portal/login', { replace: true }); };
  const active = NAV.find((n) => n.key === tab) || NAV[0];

  const go = (key) => { setTab(key); setMobileNav(false); };

  return (
    <div className="wm-layout">
      {/* Sidebar */}
      <aside className={`wm-sidebar ${mobileNav ? 'open' : ''}`}>
        <div className="wm-brand">
          <span className="brand-mark"><Icon name="tool" size={22} /></span>
          <div>
            <h1>Webmaster</h1>
            <p>JG Detailing</p>
          </div>
        </div>

        <nav className="wm-nav">
          {NAV.map((n) => (
            <button key={n.key} className={`wm-nav-item ${tab === n.key ? 'active' : ''}`} onClick={() => go(n.key)}>
              <span className="wm-nav-icon"><Icon name={n.icon} size={19} /></span>
              <span className="wm-nav-text">
                <span className="wm-nav-label">{n.label}</span>
                <span className="wm-nav-sub">{n.sub}</span>
              </span>
            </button>
          ))}
        </nav>

        <div className="wm-sidebar-foot">
          <ThemeToggle />
          <button className="admin-ghost-btn wm-viewas" onClick={() => navigate('/portal/manager')}>
            <Icon name="eye" size={16} /> View as Shop Manager
          </button>
          <button className="admin-ghost-btn wm-logout" onClick={handleLogout}><Icon name="power" size={16} /> Log out</button>
        </div>
      </aside>

      {mobileNav && <div className="wm-scrim" onClick={() => setMobileNav(false)} />}

      {/* Content */}
      <div className="wm-content">
        <header className="wm-content-head">
          <button className="wm-burger" onClick={() => setMobileNav(true)} aria-label="Open menu"><Icon name="menu" size={20} /></button>
          <div className="wm-head-title">
            <span className="wm-head-icon"><Icon name={active.icon} size={23} /></span>
            <div>
              <h2>{active.label}</h2>
              <p>{active.sub}</p>
            </div>
          </div>
          <div className="wm-head-actions" id="bookings-head-actions" />
        </header>

        <main className="wm-main">
          {tab === 'overview' && <Overview navigate={navigate} />}
          {tab === 'bookings' && <BookingsPanel allowBoard={true} />}
          {tab === 'team' && <TeamPanel />}
          {tab === 'marketing' && <MarketingPanel showPricing />}
          {tab === 'rewards' && <RewardsPanel showConfig />}
          {tab === 'deploy' && <DeployPanel />}
        </main>
      </div>
    </div>
  );
};

export default WebmasterDashboard;
